import { useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useI18nContext } from '@/i18n/i18n-react';
import { todaySoFarRows, useTodaySoFar } from '@/hooks/use-today-so-far';
import { chartColor } from '@/lib/chart-colors';
import { computeAverageTicket } from '@/lib/home-metrics';
import { mobileLayer } from '@/lib/mobile-layer';
import { computeDelta } from '@/lib/period-comparison';
import { formatCurrency } from '@/components/metrics/formatters';
import { DeltaBadge } from '@/components/metrics/delta-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const HOUR_TICKS = ['0h', '6h', '12h', '18h', '24h'];
const YESTERDAY_STROKE = '#8C94A1';

/**
 * "Hoje até HH:mm" (HOME-05): today's revenue until now, its sales and
 * average order, the variation against yesterday until the same time
 * (CMP-02), the cumulative chart (today solid, yesterday dashed gray) and
 * yesterday's closing total. Independent of the page period.
 */
export function TodaySoFarCard() {
  const { LL, locale } = useI18nContext();
  const { now, today, yesterdayUntilNow, yesterday } = useTodaySoFar();
  const time = now.toFormat('HH:mm');
  const data = today.data;

  const rows = useMemo(
    () =>
      data
        ? todaySoFarRows(
            data.revenueChart.points,
            data.totalRevenue,
            yesterday.data?.revenueChart.points,
            now
          )
        : [],
    [data, yesterday.data, now]
  );

  const body = () => {
    if (today.isError) {
      return (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">
            {LL.home.today.loadError()}
          </p>
          <Button
            type="button"
            variant="outline"
            className={mobileLayer.touch}
            onClick={() => void today.refetch()}
          >
            {LL.home.today.retry()}
          </Button>
        </div>
      );
    }
    if (!data) {
      return (
        <>
          <Skeleton className="h-9 w-36" />
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-14 w-full" />
        </>
      );
    }

    const ticket = computeAverageTicket(
      data.totalRevenue,
      data.totalPaidOrders
    );
    const base = yesterdayUntilNow.data;
    // Failed → no variation; 0 → "sem dados no período anterior" (CMP-02).
    const delta = computeDelta(
      data.totalRevenue,
      yesterdayUntilNow.isError ? undefined : base?.totalRevenue,
      locale
    );

    return (
      <>
        <p className="text-3xl font-semibold tabular-nums tracking-[-0.02em]">
          {formatCurrency(data.totalRevenue, locale, data.currency)}
        </p>
        <p className="text-sm text-muted-foreground">
          {LL.home.today.salesLine({
            count: data.totalPaidOrders,
            ticket:
              ticket === null
                ? '—'
                : formatCurrency(ticket, locale, data.currency),
          })}
        </p>
        <div className="min-h-5">
          <DeltaBadge
            delta={delta}
            comparison={
              base ? (
                <span className="text-xs text-muted-foreground">
                  {LL.home.today.comparison({
                    time,
                    value: formatCurrency(
                      base.totalRevenue,
                      locale,
                      data.currency
                    ),
                  })}
                </span>
              ) : null
            }
          />
        </div>
        <div aria-hidden="true" className="mt-1 h-14 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={rows}
              margin={{ top: 6, right: 4, bottom: 2, left: 4 }}
            >
              <XAxis type="number" dataKey="x" domain={[0, 24]} hide />
              <YAxis hide domain={[0, 'auto']} />
              <Line
                type="monotone"
                dataKey="yesterday"
                stroke={YESTERDAY_STROKE}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="today"
                stroke={chartColor(1)}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div
          aria-hidden="true"
          className="flex justify-between text-[11px] text-muted-foreground"
        >
          {HOUR_TICKS.map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-0.5 w-4"
              style={{ background: chartColor(1) }}
            />
            {LL.home.today.todayLegend()}
          </span>
          {yesterday.data ? (
            <span className="inline-flex items-center gap-2">
              <span
                aria-hidden="true"
                className="h-0 w-4 border-t-[1.5px] border-dashed"
                style={{ borderColor: YESTERDAY_STROKE }}
              />
              {LL.home.today.yesterdayClosed({
                value: formatCurrency(
                  yesterday.data.totalRevenue,
                  locale,
                  yesterday.data.currency
                ),
              })}
            </span>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <Card data-home-today>
      <CardContent className="flex flex-col gap-2 p-5 max-md:p-4">
        <p className="flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          <span
            aria-hidden="true"
            className="size-2 rounded-full bg-[#155DFC] motion-safe:animate-pulse"
          />
          {LL.home.today.title({ time })}
        </p>
        {body()}
      </CardContent>
    </Card>
  );
}
